/*! Variant Picker Availability — with programmatic change events
   - Disables/un-disables swatches based on current selection
   - Ensures a valid, available variant is always selected
   - Fires real `change`/`input` events when selection is changed in code
   - Updates [data-variant-id] on Add to Cart
   - Updates ?variant= in URL on product pages
   - Updates product price and compare-at price by fetching section HTML
   - Emits a custom `variant:change` on the picker with { variant, selectedOptions }
*/
(function () {
  "use strict";

  // --------- Config
  const PICKER_SELECTOR = ".product-variants-picker";
  const PRODUCT_JSON_SELECTOR = "#product-json";
  const ADD_TO_CART_SELECTOR = "[data-add-to-cart]";
  const MAIN_PRODUCT_SCOPE = ".main-product-section";
  const PRODUCT_PAGE_PATH_FRAGMENT = "/products/";

  // Cache parsed product JSON per picker
  const productCache = new WeakMap();

  // ---------- Utils
  function getPickerProduct(picker) {
    if (productCache.has(picker)) return productCache.get(picker);
    const node = picker.querySelector(PRODUCT_JSON_SELECTOR);
    if (!node) return null;
    try {
      const product = JSON.parse(node.textContent.trim());
      productCache.set(picker, product);
      return product;
    } catch (e) {
      console.error("[VariantPicker] Failed to parse product JSON", e);
      return null;
    }
  }

  function getFieldsets(picker) {
    return Array.from(picker.querySelectorAll("fieldset"));
  }

  function getSelectedOptions(picker) {
    return getFieldsets(picker).map((fs) => {
      const checked = fs.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  function labelForInput(input) {
    const byFor = input.ownerDocument.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    if (byFor) return byFor;
    const next = input.nextElementSibling;
    return next && next.tagName === "LABEL" ? next : null;
  }

  function anyAvailableVariant(product, candidateOptions) {
    return product.variants.some((v) => {
      if (!v.available) return false;
      for (let i = 0; i < v.options.length; i++) {
        const sel = candidateOptions[i];
        if (sel && v.options[i] !== sel) return false;
      }
      return true;
    });
  }

  function findExactAvailableVariant(product, selectedOptions) {
    return product.variants.find(
      (v) =>
        v.available &&
        v.options.length === selectedOptions.length &&
        v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  function updateUrlWithVariantId(variantId) {
    if (!String(variantId)) return;
    if (!window.location.pathname.includes(PRODUCT_PAGE_PATH_FRAGMENT)) return;
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.replaceState(null, "", url.toString());
  }

  function getAddToCartButton(picker) {
    const drawerBtn = picker.closest("sidebar-drawer")?.querySelector(ADD_TO_CART_SELECTOR);
    if (drawerBtn) return drawerBtn;
    return document.querySelector(`${MAIN_PRODUCT_SCOPE} ${ADD_TO_CART_SELECTOR}`);
  }

  function applyVariantSelection(picker, variant, selectedOptions) {
    if (!variant) return;

    const btn = getAddToCartButton(picker);
    if (btn) btn.setAttribute("data-variant-id", String(variant.id));

    updateUrlWithVariantId(variant.id);

    // Notify listeners at the component level
    picker.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        composed: true,
        detail: { variant, selectedOptions },
      })
    );
  }

  // --- NEW: programmatic setter that also emits events
  function setCheckedAndEmit(input) {
    if (!input) return;
    if (!input.checked) input.checked = true;

    // Fire both input + change so all kinds of listeners get it
    input.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
    input.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
  }

  // ---------- Reflect the currently selected value text in each legend
  function updateSelectedLegendValues(picker) {
    getFieldsets(picker).forEach((fieldset) => {
      const legendValue = fieldset.querySelector("legend .selected_variant_value");
      if (!legendValue) return;
      // const checked = fieldset.querySelector('input[type="radio"]:checked');
      // legendValue.textContent = checked ? checked.value : "";
    });
  }

  // ---------- Compute & reflect availability for each swatch
  function updateOptionAvailability(picker, product) {
    const fieldsets = getFieldsets(picker);
    const selected = getSelectedOptions(picker);

    fieldsets.forEach((fieldset, optionIndex) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

      radios.forEach((input) => {
        const candidate = selected.slice();
        candidate[optionIndex] = input.value;

        const available = anyAvailableVariant(product, candidate);
        input.disabled = !available;

        const label = labelForInput(input);
        if (label) {
          label.classList.toggle("sold-out", !available);
          label.setAttribute("aria-disabled", String(!available));
        }
      });

      // If the currently selected radio became disabled, pick first enabled and EMIT events
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked && checked.disabled) {
        const firstEnabled = radios.find((r) => !r.disabled);
        if (firstEnabled) {
          setCheckedAndEmit(firstEnabled); // <-- ensures change/input fire
        }
      }
    });
  }

  // ---------- Orchestrate a single picker update
  function handlePickerChange(picker) {
    const product = getPickerProduct(picker);
    if (!product) return;

    // 1) Reflect availability
    updateOptionAvailability(picker, product);

    // 2) Ensure we map to a valid, available variant
    let selected = getSelectedOptions(picker);
    let matched = findExactAvailableVariant(product, selected);

    if (!matched) {
      // If something's still invalid, nudge each fieldset and EMIT events
      getFieldsets(picker).forEach((fs) => {
        const current = fs.querySelector('input[type="radio"]:checked');
        if (!current || current.disabled) {
          const firstEnabled = fs.querySelector('input[type="radio"]:not(:disabled)');
          if (firstEnabled) setCheckedAndEmit(firstEnabled); // <-- emits events
        }
      });

      // Recompute after nudging
      updateOptionAvailability(picker, product);
      selected = getSelectedOptions(picker);
      matched = findExactAvailableVariant(product, selected);
    }

    if (!matched) {
      console.warn("[VariantPicker] No available variant for current selections.");
      return;
    }

    // 3) Reflect the selected option label text in legends
    updateSelectedLegendValues(picker);

    // 4) Apply + emit custom component event
    applyVariantSelection(picker, matched, selected);
  }

  // ---------- Split text content on "-" and wrap in spans
  function splitVariantTextContent(picker) {
    const textElements = picker.querySelectorAll('.value-text');
    textElements.forEach(element => {
      const text = element.textContent.trim();
      if (text.includes('-')) {
        const parts = text.split('-');
        if (parts.length === 2) {
          const leftPart = parts[0].trim();
          const rightPart = parts[1].trim();
          element.innerHTML = `<span class="variant-tabel-text">${leftPart}</span><span class="variant-value-text">${rightPart}</span>`;
        }
      }
    });
  }

  // ---------- Init a picker once
  function initPicker(picker) {
    if (!picker || picker.__variantPickerInitialized) return;
    picker.__variantPickerInitialized = true;
    getPickerProduct(picker);
    // Split text content on "-" and wrap in spans
    splitVariantTextContent(picker);
    // Initial sync (this may programmatically select radios; events will fire)
    handlePickerChange(picker);
  }

  // ---------- Global listeners

  // Use 'change' (user & programmatic via setCheckedAndEmit)
  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== "radio") return;
    const picker = target.closest(PICKER_SELECTOR);
    if (!picker) return;
    handlePickerChange(picker);
  });

  // Run on initial load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      document.querySelectorAll(PICKER_SELECTOR).forEach(initPicker);
    });
  } else {
    document.querySelectorAll(PICKER_SELECTOR).forEach(initPicker);
  }

  // Observe DOM for dynamically added pickers (e.g., quick-add drawers)
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.(PICKER_SELECTOR)) {
          initPicker(node);
          splitVariantTextContent(node);
        }
        node.querySelectorAll?.(PICKER_SELECTOR).forEach((picker) => {
          initPicker(picker);
          splitVariantTextContent(picker);
        });
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Shopify Theme Editor hooks
  document.addEventListener("shopify:section:load", () => {
    document.querySelectorAll(PICKER_SELECTOR).forEach((p) => {
      p.__variantPickerInitialized = false;
      initPicker(p);
      splitVariantTextContent(p);
    });
  });
  document.addEventListener("shopify:section:select", () => {
    document.querySelectorAll(PICKER_SELECTOR).forEach((p) => {
      handlePickerChange(p);
      splitVariantTextContent(p);
    });
  });
})();

// ------------------------
// Price update helpers
// ------------------------
(function () {
  const PRICE_SELECTOR = ".product-price";
  const COMPARE_PRICE_SELECTOR = ".product-price-discount";
  
  async function updateProductPriceFromSection(picker, variant) {
    if (!picker || !variant) return;
    
    try {
      // Get the section ID from the picker
      const sectionId = picker.dataset.sectionId;
      if (!sectionId) return;
      
      // Build the URL to fetch the section HTML
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      url.searchParams.set('section_id', sectionId);
      
      // Fetch the section HTML
      const response = await fetch(url.toString());
      if (!response.ok) return;
      
      const html = await response.text();
      
      // Create a temporary DOM element to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Find the price elements in the fetched HTML
      const newPriceEl = tempDiv.querySelector(PRICE_SELECTOR);
      const newComparePriceEl = tempDiv.querySelector(COMPARE_PRICE_SELECTOR);
      
      if (!newPriceEl) return;
      
      // Find the current price elements in the actual DOM
      const scope = picker.closest(".main-product-section, sidebar-drawer") || document;
      const currentPriceEl = scope.querySelector(PRICE_SELECTOR);
      const currentComparePriceEl = scope.querySelector(COMPARE_PRICE_SELECTOR);
      
      // Update the price
      if (currentPriceEl) {
        currentPriceEl.textContent = newPriceEl.textContent;
      }
      
      // Update the compare price
      if (currentComparePriceEl) {
        if (newComparePriceEl && newComparePriceEl.textContent.trim()) {
          currentComparePriceEl.textContent = newComparePriceEl.textContent;
          currentComparePriceEl.style.display = 'inline';
        } else {
          currentComparePriceEl.style.display = 'none';
        }
      }
      
    } catch (error) {
      console.error('[VariantPicker] Failed to update price from section:', error);
    }
  }
  
  // Make function globally available
  window.updateProductPriceFromSection = updateProductPriceFromSection;
})();

// ------------------------
// Media sync helpers
// ------------------------
(function () {
  const CAROUSEL_SELECTOR = ".product-media-carousel embla-carousel[data-embla-carousel]";
  const SCOPE_SELECTOR = ".main-product-section, sidebar-drawer";

  const getProduct = (picker) => {
    const node = picker?.querySelector?.("#product-json");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent.trim());
    } catch {
      return null;
    }
  };

  const getMediaId = (product, variant) => {
    if (!product || !variant) return null;
    if (variant.featured_media?.id) return variant.featured_media.id;
    const vSrc = variant.featured_image?.src || variant.image?.src || variant.image;
    if (!vSrc || !Array.isArray(product.media)) return null;
    const normalized = String(vSrc).split("?")[0];
    const match = product.media.find((m) => {
      const src = m?.src || m?.preview_image?.src || "";
      return src && String(src).split("?")[0] === normalized;
    });
    return match?.id || null;
  };

  const scrollToMedia = (picker, mediaId, tries = 0) => {
    if (!picker || !mediaId) return;
    const scope = picker.closest(SCOPE_SELECTOR) || document;
    const carousel = scope.querySelector(CAROUSEL_SELECTOR);
    if (!carousel) return;
    const slides = Array.from(carousel.querySelectorAll('.embla__slide[data-media-id]'));
    const index = slides.findIndex((el) => String(el.dataset.mediaId) === String(mediaId));
    if (index < 0) return;
    const embla = carousel._emblaInstance?.embla;
    if (embla?.scrollTo) return embla.scrollTo(index);
    if (tries < 10) return setTimeout(() => scrollToMedia(picker, mediaId, tries + 1), 50);
    slides[index].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  };

  const resolveCurrentVariant = (picker, product) => {
    let variantId = null;
    try {
      variantId = new URL(window.location.href).searchParams.get("variant");
    } catch {}
    if (!variantId) {
      const btn = (picker.closest(SCOPE_SELECTOR) || document).querySelector("[data-add-to-cart][data-variant-id]");
      variantId = btn?.dataset?.variantId || null;
    }
    return product?.variants?.find((v) => String(v.id) === String(variantId)) || null;
  };

  const syncPicker = (picker) => {
    const product = getProduct(picker);
    if (!product) return;
    const variant = resolveCurrentVariant(picker, product);
    const mediaId = getMediaId(product, variant);
    if (mediaId) scrollToMedia(picker, mediaId);
    
    // Also sync price on initial load
    if (variant && window.updateProductPriceFromSection) {
      window.updateProductPriceFromSection(picker, variant);
    }

    // sync variant media
    fetchVariantMedia(picker, variant);
  };

  const fetchVariantMedia = (picker, variant) => {
    if (!picker || !variant) return;
  
    const variantMetafields = JSON.parse(document.getElementById('VariantMetafieldsJSON').textContent || '{}');
    const variantData = variantMetafields[variant.id];
    const product = getProduct(picker);
    const scope = picker.closest(SCOPE_SELECTOR) || document;
  
    // Check if variant has custom media, otherwise use default product media
    let mediaToUse = null;
    let isCustomMedia = false;
    
    if (variantData && variantData.media && variantData.media.length > 0) {
      mediaToUse = variantData.media;
      isCustomMedia = true;
    } else if (product && product.media && product.media.length > 0) {
      // Fallback to default product media
      mediaToUse = product.media.map(media => ({
        src: media.src || media.preview_image?.src,
        mime_type: media.media_type === 'video' ? 'video/mp4' : 'image/jpeg'
      }));
      isCustomMedia = false;
    }
  
    if (!mediaToUse || mediaToUse.length === 0) {
      console.warn('No media found for this variant:', variant.title);
      return;
    }
  
    const container = scope.querySelector('#VariantMediaContainer');
    if (!container) return;
  
    const slidesHTML = mediaToUse.map((file, index) => {
      const src = file.src.replace(/\\\//g, '/'); // fix escaped slashes
      const mediaId = isCustomMedia ? `variant-${variant.id}-${index}` : `product-${index}`;
      return `
        <embla-slide data-media-id="${mediaId}">
          <span class="image-wrapper">
            <img src="${src}" alt="${variant.title || 'Product image'}" loading="lazy">
          </span>
        </embla-slide>`;
    }).join('');
  
    container.innerHTML = slidesHTML;
  
    // Update thumbnail list to match the new media
    updateThumbnailList(mediaToUse, variant.id, isCustomMedia, scope);

    // Reset carousel to first slide when variant changes
    const carousel = scope.querySelector(CAROUSEL_SELECTOR);
    if (carousel && carousel._emblaInstance?.embla) {
      carousel._emblaInstance.embla.scrollTo(0);
    }
  };

  const updateThumbnailList = (variantMedia, variantId, isCustomMedia = true, scope = document) => {
    const thumbnailList = scope.querySelector('#ProductMediaThumbnails');
    if (!thumbnailList || !variantMedia || variantMedia.length === 0) return;

    // Generate new thumbnail HTML
    const thumbnailsHTML = variantMedia.map((file, index) => {
      const src = file.src.replace(/\\\//g, '/'); // fix escaped slashes
      const mediaId = isCustomMedia ? `variant-${variantId}-${index}` : `product-${index}`;
      const activeClass = index === 0 ? 'active' : '';
      
      return `
        <li class="${activeClass}" data-media-id="${mediaId}">
          <button>
            <img src="${src}" alt="Product image" loading="lazy">
          </button>
        </li>`;
    }).join('');

    // Update the thumbnail list
    thumbnailList.innerHTML = thumbnailsHTML;

    // Re-attach click handlers to new thumbnails
    attachThumbnailHandlers(scope);
  };

  const attachThumbnailHandlers = (scope = document) => {
    const thumbnails = scope.querySelectorAll("#ProductMediaThumbnails li");
    const carouselElement = scope.querySelector(".product-media-carousel embla-carousel");
    
    if (!thumbnails || thumbnails.length === 0 || !carouselElement) return;

    // Wait for Embla to be available
    const waitForEmbla = () => {
      return new Promise((resolve) => {
        const checkEmbla = () => {
          if (carouselElement._emblaInstance?.embla) {
            resolve(carouselElement._emblaInstance.embla);
          } else {
            setTimeout(checkEmbla, 50);
          }
        };
        checkEmbla();
      });
    };

    waitForEmbla().then((emblaApi) => {
      // Add click handlers to thumbnails
      thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener("click", () => {
          emblaApi.scrollTo(index);
        });
      });

      // Update active thumbnail based on carousel position
      const updateActiveThumbnail = () => {
        const currentIndex = emblaApi.selectedScrollSnap();
        thumbnails.forEach((thumbnail, index) => {
          thumbnail.classList.toggle("active", index === currentIndex);
        });
      };

      // Set initial active state
      updateActiveThumbnail();

      // Listen for carousel changes
      emblaApi.on("select", updateActiveThumbnail);
    });
  };

  // Respond to variant changes
  document.addEventListener("variant:change", (e) => {
    const picker = e.target?.closest?.(".product-variants-picker");
    const variant = e.detail?.variant;
    if (!picker || !variant) return;
    
    // Update price by fetching section HTML
    updateProductPriceFromSection(picker, variant);
    fetchVariantMedia(picker, variant);
  });

  // Initial syncs (after Embla init and on Theme Editor events)
  const init = (root = document) => {
    root.querySelectorAll?.(".product-variants-picker")?.forEach((picker) => {
      setTimeout(() => syncPicker(picker), 0);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", (e) => init(e.target));
  document.addEventListener("shopify:section:select", (e) => init(e.target));
})();